/**
 * IP Access Control Editor component for configuring per-port IP rules.
 *
 * @module components/ui/IpAccessControlEditor
 */

import { useId } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
import { TagInput } from './TagInput';
import type { IpAccessControlAction, PortIpAccessControlRule } from '@/types';
import {
  MAX_IP_RULES,
  MAX_CIDRS_PER_RULE,
  MAX_SERVICE_TAGS_PER_RULE,
  SUPPORTED_SERVICE_TAG,
  validateCidr,
  validateServiceTag,
  validateIpRuleSelectors,
} from '@/utils/ipAccessControl';

export interface IpAccessControlEditorProps {
  /** The default action when no rules match */
  defaultAction: IpAccessControlAction;
  /** Callback when the default action changes */
  onDefaultActionChange: (action: IpAccessControlAction) => void;
  /** The list of IP access control rules */
  rules: PortIpAccessControlRule[];
  /** Callback when rules change */
  onRulesChange: (rules: PortIpAccessControlRule[]) => void;
}

/**
 * A reusable component for editing IP access control settings.
 * Includes a default action selector and a dynamic list of IP rules.
 */
export function IpAccessControlEditor({
  defaultAction,
  onDefaultActionChange,
  rules,
  onRulesChange,
}: IpAccessControlEditorProps) {
  const editorId = useId();
  const handleAddRule = () => {
    if (rules.length >= MAX_IP_RULES) return;
    const nextPriority = rules.length > 0 ? Math.max(...rules.map((r) => r.priority)) + 100 : 100;
    onRulesChange([
      ...rules,
      { name: '', action: 'Allow', priority: nextPriority, sourceCidrs: [] },
    ]);
  };

  const handleRemoveRule = (index: number) => {
    onRulesChange(rules.filter((_, i) => i !== index));
  };

  const handleRuleFieldChange = (
    index: number,
    field: 'name' | 'action' | 'priority',
    value: string
  ) => {
    onRulesChange(
      rules.map((rule, i) => {
        if (i !== index) return rule;
        if (field === 'name') return { ...rule, name: value };
        if (field === 'action') return { ...rule, action: value as IpAccessControlAction };
        if (field === 'priority') return { ...rule, priority: parseInt(value) || 0 };
        return rule;
      })
    );
  };

  const handleCidrsChange = (index: number, cidrs: string[]) => {
    onRulesChange(rules.map((rule, i) => (i === index ? { ...rule, sourceCidrs: cidrs } : rule)));
  };

  const handleSelectorChange = (index: number, selector: string) => {
    onRulesChange(
      rules.map((rule, i) =>
        i === index
          ? {
              ...rule,
              sourceCidrs: selector === 'cidrs' ? [] : undefined,
              sourceServiceTags: selector === 'tags' ? [] : undefined,
            }
          : rule
      )
    );
  };

  return (
    <div className="space-y-6">
      <Select
        id={`${editorId}-default-action`}
        label="Default Action"
        value={defaultAction}
        onChange={(e) => onDefaultActionChange(e.target.value as IpAccessControlAction)}
        options={[
          { value: 'Allow', label: 'Allow - Allow all IPs by default' },
          { value: 'Deny', label: 'Deny - Block all IPs by default' },
        ]}
      />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">IP Rules</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Rules may use CIDR ranges or service tags. Lower priorities run first; the first match
              wins.
            </div>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={handleAddRule}
            disabled={rules.length >= MAX_IP_RULES}
          >
            Add Rule
          </Button>
        </div>

        {rules.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No rules. The default action will apply to all IP addresses.
          </p>
        ) : (
          <div className="space-y-3">
            {rules.map((rule, index) => {
              const cidrs = rule.sourceCidrs ?? [];
              const tags = rule.sourceServiceTags ?? [];
              const hasBothSelectors = cidrs.length > 0 && tags.length > 0;
              const selector =
                tags.length > 0 || (rule.sourceServiceTags !== undefined && cidrs.length === 0)
                  ? 'tags'
                  : 'cidrs';
              const selectorError = validateIpRuleSelectors(rule);
              return (
                <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-3">
                  {/* Rule header with remove button - always visible */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Rule {index + 1}
                    </span>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRemoveRule(index)}
                      title="Remove rule"
                      aria-label={`Remove rule ${index + 1}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Input
                        id={`${editorId}-${index}-name`}
                        label="Name"
                        placeholder="rule-name"
                        value={rule.name}
                        onChange={(e) => handleRuleFieldChange(index, 'name', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Select
                        id={`${editorId}-${index}-action`}
                        label="Action"
                        value={rule.action}
                        onChange={(e) => handleRuleFieldChange(index, 'action', e.target.value)}
                        options={[
                          { value: 'Allow', label: 'Allow' },
                          { value: 'Deny', label: 'Deny' },
                        ]}
                      />
                    </div>
                    <div>
                      <Input
                        id={`${editorId}-${index}-priority`}
                        label="Priority"
                        type="number"
                        placeholder="100"
                        value={String(rule.priority)}
                        onChange={(e) => handleRuleFieldChange(index, 'priority', e.target.value)}
                        min={0}
                        max={1000}
                        required
                      />
                    </div>
                  </div>
                  <Select
                    id={`${editorId}-${index}-selector`}
                    label="Source Type"
                    value={selector}
                    onChange={(e) => handleSelectorChange(index, e.target.value)}
                    options={[
                      { value: 'cidrs', label: 'CIDR ranges' },
                      { value: 'tags', label: 'Service tags (candidate rollout)' },
                    ]}
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Switching source type clears the previous source list.
                  </p>
                  {(selector === 'cidrs' || hasBothSelectors) && (
                    <TagInput
                      key="cidrs"
                      label="Source CIDRs"
                      value={cidrs}
                      onChange={(cidrs) => handleCidrsChange(index, cidrs)}
                      placeholder="10.0.0.0/8"
                      helperText={
                        cidrs.length >= MAX_CIDRS_PER_RULE
                          ? `Maximum ${MAX_CIDRS_PER_RULE} CIDRs per rule`
                          : 'Enter IPv4 or IPv6 CIDR ranges (e.g., 192.168.1.0/24 or 2001:db8::/32)'
                      }
                      validate={(value) =>
                        cidrs.length >= MAX_CIDRS_PER_RULE
                          ? `Maximum ${MAX_CIDRS_PER_RULE} CIDRs per rule`
                          : validateCidr(value)
                      }
                    />
                  )}
                  {(selector === 'tags' || hasBothSelectors) && (
                    <TagInput
                      key="tags"
                      label="Source Service Tags"
                      value={tags}
                      onChange={(sourceServiceTags) =>
                        onRulesChange(
                          rules.map((current, i) =>
                            i === index
                              ? {
                                  ...current,
                                  sourceServiceTags: sourceServiceTags.map((tag) =>
                                    validateServiceTag(tag) === null ? SUPPORTED_SERVICE_TAG : tag
                                  ),
                                }
                              : current
                          )
                        )
                      }
                      placeholder={SUPPORTED_SERVICE_TAG}
                      helperText={`Initial catalog: candidate data, not production-approved. Supported name: ${SUPPORTED_SERVICE_TAG}. Disabled by default; availability requires administrator enablement for the target deployment. Maximum ${MAX_SERVICE_TAGS_PER_RULE} distinct references.`}
                      validate={(value) =>
                        tags.length >= MAX_SERVICE_TAGS_PER_RULE
                          ? `Maximum ${MAX_SERVICE_TAGS_PER_RULE} service tags per rule`
                          : validateServiceTag(value)
                      }
                    />
                  )}
                  {selectorError && (
                    <p role="alert" className="text-sm text-red-600 dark:text-red-400 break-words">
                      {selectorError}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
